<script lang="ts" context="module">
	export type BlockType =
		| 'link'
		| 'image'
		| 'document'
		| 'media'
		| 'gallery'
		| 'widget'
		| 'customHtml'
		| 'quickPoll';

	export const allBlocks: BlockType[] = [
		'link',
		'image',
		'document',
		'media',
		'gallery',
		'widget',
		'customHtml',
		'quickPoll',
	];
</script>

<script lang="ts">
	import FileUploader from '$lib/components/FileUploader/FileUploader.svelte';
	import { _ } from 'svelte-i18n';
	import Popover from '$lib/components/Popover/Popover.svelte';
	import type { Editor } from 'svelte-tiptap';
	import type { Icon as IconType } from '$lib/components/Icon/Icon-types';
	import Icon from '$lib/components/Icon/Icon.svelte';
	import { getHubStores } from '$lib/stores/hubStores';
	import { atLeastModerator } from '$lib/utils/roles';

	const { hub } = getHubStores();

	export let enabledBlocks: BlockType[] = allBlocks;
	export let editor: Editor;

	let activeBlocks: BlockType[] = $hub.settings.feature.nextGenUI
		? enabledBlocks.filter((b) => b != 'widget')
		: enabledBlocks.filter((b) => b != 'customHtml');

	const isModerator = $hub.user_context.hub_role == 'moderator';
	const isOrgAdmin = $hub.user_context.hub_role == 'orgadmin';

	const disableCustomHTML =
		!atLeastModerator($hub.user_context) || // not at least moderator
		(isModerator && $hub.settings.feature.disable_custom_html) || // moderator and custom html disabled
		isOrgAdmin; // org admin

	if (disableCustomHTML && $hub.settings.feature.nextGenUI) {
		activeBlocks = activeBlocks.filter((b) => b != 'customHtml');
	}

	if ($hub.settings.feature.disableDocumentFileUploads) {
		activeBlocks = activeBlocks.filter((b) => b != 'document' && b != 'media');
	}

	// disable quick poll if not moderator or svelte not enabled
	if (!$hub.settings.feature.nextGenUI || !atLeastModerator($hub.user_context)) {
		activeBlocks = activeBlocks.filter((b) => b != 'quickPoll');
	}

	type Items = {
		[key in BlockType]: {
			icon: IconType;
			text: string;
		};
	};

	const items: Items = {
		link: { icon: 'link', text: $_('composer.toolbar.embed.link') },
		image: { icon: 'add-image', text: $_('composer.toolbar.embed.image') },
		document: { icon: 'upload', text: $_('composer.toolbar.embed.document') },
		media: { icon: 'media', text: $_('composer.toolbar.embed.media') },
		gallery: { icon: 'grid', text: $_('composer.toolbar.embed.gallery') },
		widget: { icon: 'ctas', text: $_('composer.toolbar.embed.widget') },
		customHtml: { icon: 'ctas', text: $_('hub.settings.cta.widget.iframe_in_adtag') },
		quickPoll: { icon: 'poll', text: 'Quick Poll' },
	};

	let isHovering = false;

	const addBlock = (block: BlockType) => {
		editor.commands.insertContent([
			{
				type: `hubs${block.charAt(0).toUpperCase() + block.slice(1)}Component`,
				attrs: { post: null, url: '' },
			},
		]);
	};
</script>

<div>
	<Popover position="inline-end" color="#2a3039">
		<ul slot="content" class="newBlock">
			{#each activeBlocks as block}
				{#if block === 'gallery'}
					<li>
						<button type="button" class="menuItem">
							<FileUploader
								on:fileUploaded={() => addBlock(block)}
								accept=".jpg, .jpeg, .gif, .png, .webp"
								multiple={true}>
								<Icon icon="grid" size="24" color="white" />
								<span>{$_('composer.toolbar.embed.gallery')}</span>
							</FileUploader>
						</button>
					</li>
				{:else if block === 'image'}
					<li>
						<button type="button" class="menuItem">
							<FileUploader
								on:fileUploaded={() => addBlock(block)}
								accept=".jpg, .jpeg, .gif, .png, .webp"
								multiple={false}>
								<Icon icon={items[block].icon} size="24" color="white" />
								<span>{items[block].text}</span>
							</FileUploader>
						</button>
					</li>
				{:else if block === 'media'}
					<li>
						<button type="button" class="menuItem">
							<FileUploader
								on:fileUploaded={() => addBlock('document')}
								accept=".mpg, .mov, .mpeg, .m4v, .mp4, .mp3"
								multiple={false}>
								<Icon icon={items[block].icon} size="24" color="white" />
								<span>{items[block].text}</span>
							</FileUploader>
						</button>
					</li>
				{:else if block === 'document'}
					<li>
						<button type="button" class="menuItem">
							<FileUploader
								on:fileUploaded={() => addBlock(block)}
								accept=".pdf, .docx, .pptx"
								multiple={false}>
								<Icon icon={items[block].icon} size="24" color="white" />
								<span>{items[block].text}</span>
							</FileUploader>
						</button>
					</li>
				{:else}
					<li>
						<button type="button" on:click={() => addBlock(block)} class="menuItem">
							<Icon icon={items[block].icon} size="24" color="white" />
							<span>{items[block].text}</span>
						</button>
					</li>
				{/if}
			{/each}
		</ul>
		<div slot="target" class="addButtonWrapper">
			<button
				type="button"
				class="plus"
				on:mouseover={() => {
					isHovering = true;
				}}
				on:mouseout={() => {
					isHovering = false;
				}}
				on:focus={() => {
					isHovering = true;
				}}
				on:focusout={() => {
					isHovering = false;
				}}
				on:blur={null}
				aria-label="insert media element">
				<Icon icon="add" color={!isHovering ? '#3f8ce8' : 'white'} />
			</button>
		</div>
	</Popover>
</div>

<style>
	/* Move popover up */
	div :global(.placeholder .content) {
		inset-block-start: -90px !important;
	}

	button {
		all: unset;
		cursor: pointer;
	}

	.plus {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--gutterSmaller);
		border: 2px solid #3f8ce8;
		border-radius: 9999px;
		font-size: larger;
		font-weight: bold;
	}

	.plus:hover,
	.plus:focus {
		background-color: #3f8ce8;
	}

	.addButtonWrapper:focus-within {
		border: 2px solid var(--darkenedPrimaryAccentColor);
	}

	ul {
		background-color: #2a3039;
	}

	li {
		border-block-end: 1px solid black;
		cursor: pointer;
	}

	li span {
		margin-block: 0 !important;
		margin-inline: var(--gutterSmall) !important;
		color: white;
	}

	li:last-child {
		border-block-end: none;
	}

	.menuItem {
		display: flex;
		align-items: center;
		justify-content: flex-start;
		inline-size: 200px;
		padding-block: var(--gutterSmall);
		padding-inline: var(--gutterMedium);
		color: white;
		font-size: var(--fontSizeSmall);
		text-decoration: none;
	}

	:global(.newBlock li .menuItem:hover span) {
		color: #3f8ce8 !important;
		cursor: pointer;
	}
</style>
